/**
 * Espaces élève / prof / parent / curieux : la PAGE ne défile jamais (seul le
 * `<main>` défile), sinon iOS laisse la page décalée après la fermeture du
 * clavier et la barre du bas « fixée » reste au milieu de l'écran (décalage =
 * hauteur du clavier, Safari ET Chrome iPhone).
 *
 * - `useAppShellDocument()` (layouts) pose `.jr-app-shell` sur <html> : la
 *   page est verrouillée à la hauteur de l'écran (cf. styles/globals.css).
 * - `initKeyboardViewportFix()` (main.tsx) : à la fermeture du clavier, si la
 *   page verrouillée est restée décalée, elle revient en haut. Et à TOUT
 *   moment hors saisie : un défilement programmé (`scrollIntoView` de la ligne
 *   en cours de la feuille de brouillon, d'une question…) fait aussi défiler
 *   la page verrouillée (`overflow: hidden` n'empêche pas un défilement par
 *   le code) — le dock du bas se retrouvait au milieu, du vide en dessous.
 */
import { useEffect } from 'react';

export const APP_SHELL_CLASS = 'jr-app-shell';
/** Agrandissement de l'écran visible au-delà duquel on considère le clavier fermé. */
export const KEYBOARD_CLOSE_MIN_DELTA = 120;

export function useAppShellDocument(): void {
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add(APP_SHELL_CLASS);
    return () => html.classList.remove(APP_SHELL_CLASS);
  }, []);
}

/** Le clavier vient de se fermer : l'écran visible a grandi d'au moins ~un clavier. */
export function keyboardJustClosed(previousHeight: number, height: number): boolean {
  return height - previousHeight >= KEYBOARD_CLOSE_MIN_DELTA;
}

/** Un champ texte a le focus : le clavier natif est (ou va être) ouvert, iOS décale la page exprès. */
export function isTyping(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement)
    return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'file', 'color'].includes(
      el.type,
    );
  return (el as HTMLElement).isContentEditable === true;
}

/** Page verrouillée restée décalée → on la remet en haut. */
export function resetShellScroll(
  win: Pick<Window, 'scrollY' | 'scrollTo'>,
  doc: Document,
): boolean {
  if (!doc.documentElement.classList.contains(APP_SHELL_CLASS)) return false;
  if (win.scrollY === 0) return false;
  win.scrollTo(0, 0);
  return true;
}

export function initKeyboardViewportFix(): void {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (!vv) return;
  let last = vv.height;
  vv.addEventListener('resize', () => {
    const h = vv.height;
    if (keyboardJustClosed(last, h)) {
      requestAnimationFrame(() => resetShellScroll(window, document));
    }
    last = h;
  });
  // Un défilement par le code a décalé la page verrouillée (hors saisie) : elle revient en haut.
  window.addEventListener(
    'scroll',
    () => {
      if (!document.documentElement.classList.contains(APP_SHELL_CLASS)) return;
      if (window.scrollY === 0 || isTyping(document.activeElement)) return;
      requestAnimationFrame(() => {
        if (!isTyping(document.activeElement)) resetShellScroll(window, document);
      });
    },
    { passive: true },
  );
  // Filet de sécurité : un champ qui perd le focus (clavier qui se ferme).
  document.addEventListener('focusout', () => {
    setTimeout(() => {
      const el = document.activeElement;
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
      if (!typing) resetShellScroll(window, document);
    }, 250);
  });
}
